import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef} from 'react'
import {Time} from 'yougile-util'
import {ChatMessage, messageReadThreshold, Voting} from 'yougile-model'
import {Sync} from '@client/sync'
import {useOnAvatarClick} from '@components/common/base/avatar'
import {ChatMessageBase} from '@components/common/base/chat-message'
import {markUnreadUpdater} from '@components/common/base/chat-message/mark-important-button'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {useChatStore} from '../../chat-store'
import {useChatLabelsStore} from '../chat-labels-store'
import {
  useEmojiReactions,
  useForwardedMessageClick,
  useForwardedMessageData,
  useMessageActions,
  useMessageData,
  useReplyTarget,
  useSeenBy
} from './utils'

type Props = {
  msg: ChatMessage
  className?: string
  fullView?: boolean
  hideAvatar?: boolean
  animateShow?: boolean
  lastImage?: boolean
}

export const UserMessage = observer(
  ({msg, className, fullView, hideAvatar, animateShow, lastImage}: Props) => {
    const [state, store] = useChatStore()
    const [labelsState] = useChatLabelsStore()
    const box = Sync.getOrCurrent(state.hub)
    const forceUpdate = useForceUpdate()

    useSyncEvent(
      'current',
      {filter: state.hub?.id, debounceTime: 500},
      () => {
        forceUpdate()
      },
      [state.hub?.id]
    )

    const messageActions = useMessageActions(msg)
    const {replyTarget, goToReplyTarget} = useReplyTarget(msg, state.hub)
    const {seenBy, seenByTitle} = useSeenBy(msg, state.hub, store.isComments)
    const messageData = useMessageData({
      box,
      msg,
      task: state.hub
    })

    const replyMessageData = useMessageData({box, msg: replyTarget})
    const forwardedMessageClick = useForwardedMessageClick(msg)
    const forwardedMessageData = useForwardedMessageData(box, msg)

    const {reactions, onReactionSelect} = useEmojiReactions({
      message: msg,
      hub: state.hub
    })

    const avatarRef = useRef<Element>(null)
    const {onClick: onAvatarClick} = useOnAvatarClick({
      userId: messageData?.user?.userId,
      ref: avatarRef,
      withUserInfo: true
    })

    const user = Sync.getCurrentUser()
    const canMarkUnread =
      !store.isComments &&
      !store.isFeedback &&
      msg.from !== globals.currentUserId &&
      // Messages marked as unread must also be in constraints of task-chat-proxy.ts
      // `getUnseenMessages` and chat.ts `newMessagesFor`
      msg.timestamp >
        Math.max(user.timestamp, Time.now() - messageReadThreshold)

    const userId = globals.currentUserId
    const isMarkUnreadActive =
      store.state.hub &&
      messageData &&
      store.state.hub?.data?.markImportantMap?.[userId] ===
        messageData.timestamp

    useEffect(() => {
      forceUpdate()
    }, [markUnreadUpdater.state.modified])

    const voting = msg.properties?.voting as Voting

    return messageData ? (
      <ChatMessageBase
        className={cn(className, 'message-item-new')}
        messageData={messageData}
        replyMessageData={replyMessageData}
        forwardedMessageData={forwardedMessageData}
        fullView={fullView}
        hideAvatar={hideAvatar}
        seenBy={seenBy}
        seenByTitle={seenByTitle}
        onRepliedMessageClick={goToReplyTarget}
        onForwardedMessageClick={forwardedMessageClick}
        labelEditing={labelsState.edited?.id === msg.id}
        {...messageActions}
        reactions={reactions}
        currentUserId={globals.currentUserId}
        onReactionSelect={onReactionSelect}
        animateShow={animateShow}
        avatarRef={avatarRef}
        onAvatarClick={onAvatarClick}
        editing={state.messageToEdit?.id === msg.id}
        lastImage={lastImage}
        canMarkUnread={canMarkUnread}
        isMarkUnreadActive={isMarkUnreadActive}
        voting={voting}
        msgTs={msg.timestamp}
        hubId={state.hub?.id}
      />
    ) : null
  }
)
